import { analyticsService } from "@/services/AnalyticsService";
import {FormattedChartData} from "@/models/FormattedChartData";
import {i18n, i18n as $t} from '@/lang';
import {toast} from "vue3-toastify";

let dashboardRequestId = 0;
let orderedProductsRequestId = 0;
let orderedProductsPendingKey = null;
let accountsRequestId = 0;
let accountsPendingKey = null;
let accountSummaryRequestId = 0;

function orderedProductsKey(state) {
    return JSON.stringify([state.queryStartDateTime, state.queryEndDateTime]);
}

function accountsKey(state) {
    return JSON.stringify([state.queryStartDateTime, state.queryEndDateTime, state.queryUserId, state.queryShiftId]);
}

const state = {
    paymentsData: {},
    orderedProductsData: null,
    orderedProductsDataKey: null,
    accountsData: null,
    accountsDataKey: null,
    paymentsRequest: {
        loading: false
    },
    orderedProductsRequest: {
        loading: false
    },
    accountsRequest: {
        loading: false
    },
    clientAccountSummaryRequest: {
        loading: false
    },
    dashboardRequest: {
        loading: false,
        data: null
    },
    dashboardPartnerSourceRequest: {
        loading: false,
        data: null
    },
    queryStartDateTime: 0,
    queryEndDateTime: 0,
    queryUserId: null,
    queryShiftId: null,
    queryUserName: null,
    clientAccountSummaryData : null,
    currentTimeInterval: null,
    dashboardAccountsChartData: null,
    dashboardSalesChartData: null,
    dashboardDailySalesFormattedChartData: null,
    dashboardRushFormattedChartData: null,
    dashboardTopSalesFormattedChartData: null,
    dashboardTopSalesByDeviceFormattedChartData: null,
    rawDashboardData: null,

    productGroupsDashboardData: null,

    allOrgs: null,
    deviceModelChartData: null,
    shiftOpen: null,

    message: null,
    firstLoading: true,
    shouldWeLoadAgain: true,
}

const actions = {
    sendInvoice({commit, state}, { payload, accountId }) {
        state.message = null
        return analyticsService.sendInvoice(accountId, payload)
            .then(() => {
                toast.success(i18n.global.t("toast.successEdit"));
                return true
            })
            .catch((error) => {
                commit("setMessage", error)
                return false
            });
    },
    sendDigitalVatTicket({commit, state}, { uuid, email }) {
        state.message = null
        return analyticsService.sendDigitalVatTicket(uuid, email)
            .then((data) => {
                if (email) {
                    toast.success(i18n.global.t("toast.vatTicketSent"));
                    return true
                }
                return data
            })
            .catch((error) => {
                commit("setMessage", error)
                return false
            });
    },
    getDashboardDataByPartnerSource({commit}) {
        commit('setDashboardPartnerSourceRequest', { loading: true, data: null, message: null });
        analyticsService.getDashboardDataByPartnerSource()
            .then(response => {
                commit('setDashboardDataPartnerSource', response.data);
                commit('setDashboardPartnerSourceRequest', { loading: false, data: null, message: null });
            })
            .catch((error) => {
                commit('setDashboardPartnerSourceRequest', { loading: false, data: null, message: error });
            });
    },
    getDashboardData({ commit, state }, payload) {
        const { timeInterval, showSkeleton = false, startDate = null, endDate = null } =
            typeof payload === 'string' ? { timeInterval: payload, showSkeleton: false } : payload;

        state.currentTimeInterval = timeInterval;
        commit('setDashboardRequest', { loading: true, data: null, message: null });

        if (showSkeleton) {
            commit('setFirstLoading', true);
        }

        const requestId = ++dashboardRequestId;
        return analyticsService.getDashboardData(timeInterval, startDate, endDate)
            .then((response) => {
                if (requestId !== dashboardRequestId) return null;
                const data = response?.data;

                const groups = Array.isArray(data?.dashboardGroups) && data.dashboardGroups.length
                    ? data.dashboardGroups
                    : null;
                commit('setProductGroupsDashboardData', groups);

                const hasDashboardBlock = !!data?.accountsData;
                if (!hasDashboardBlock || (timeInterval !== 'CUSTOM' && !data.shift)) {
                    commit('resetDashboardData');
                } else {
                    commit('setDashboardRequest', { loading: false, data: data, message: null });
                    commit('setDashboardChartData', data);
                }
                commit('setFirstLoading', false);
                return data;
            })
            .catch((error) => {
                if (requestId !== dashboardRequestId) return null;
                commit('resetDashboardData');
                commit('setProductGroupsDashboardData', null);
                commit('setDashboardRequest', { loading: false, data: null, message: error });
                commit('setFirstLoading', false);
                return null;
            });
    },
    getPaymentTransactions({commit, state}) {
        commit('setPaymentsRequest', {loading: true});
        //console.log(state.queryStartDateTime, state.queryEndDateTime);
        analyticsService.getPaymentTransactions(state.queryStartDateTime, state.queryEndDateTime)
            .then((response) => {
                commit('setPaymentsRequest', {loading: false});
                commit('updatePaymentsData', response.data);
            })
            .catch((error) => commit('setPaymentsRequest', {loading: false, message: error}));
    },
    getOrderedProductsHistory({commit, state}, options = false) {
        const boolean = options === true || options?.clear === true;
        const key = orderedProductsKey(state);
        if (orderedProductsPendingKey !== null && options?.force !== true) return;
        const requestId = ++orderedProductsRequestId;
        orderedProductsPendingKey = key;
        commit('setOrderedProductsRequest', {loading: true});
        if (boolean || state.orderedProductsDataKey !== key){
            commit('updateOrderedProductsData', null);
            commit('setOrderedProductsDataKey', null);
        }
        analyticsService.getOrderedProductsHistory(state.queryStartDateTime, state.queryEndDateTime)
            .then((response) => {
                if (requestId !== orderedProductsRequestId) return;
                orderedProductsPendingKey = null;
                commit('setOrderedProductsRequest', {loading: false});
                commit('updateOrderedProductsData', response.data);
                commit('setOrderedProductsDataKey', key);
            })
            .catch((error) => {
                if (requestId !== orderedProductsRequestId) return;
                orderedProductsPendingKey = null;
                commit('setOrderedProductsRequest', {loading: false, message: error});
            });
    },
    getAccountsHistory({commit, state}, options = false) {
        const boolean = options === true || options?.clear === true;
        const key = accountsKey(state);
        if (accountsPendingKey !== null && options?.force !== true) return;
        const requestId = ++accountsRequestId;
        accountsPendingKey = key;
        commit('setAccountsRequest', {loading: true});
        if (boolean || state.accountsDataKey !== key){
            commit('updateAccountsData', null);
            commit('setAccountsDataKey', null);
        }
        analyticsService.getClientAccountsHistory(state.queryStartDateTime, state.queryEndDateTime, {
            userId: state.queryUserId,
            shiftId: state.queryShiftId,
        })
            .then((response) => {
                if (requestId !== accountsRequestId) return;
                accountsPendingKey = null;
                const clientAccountsWithCheck = response.data.clientAccounts?.map(row =>  ({
                    ...row,
                    checked: false
                }))
                if (response.data.clientAccounts){
                    response.data.clientAccounts = clientAccountsWithCheck
                }
                commit('updateAccountsData', response.data);
                commit('setAccountsDataKey', key);
                commit('setAccountsRequest', {loading: false});
            })
            .catch((error) => {
                if (requestId !== accountsRequestId) return;
                accountsPendingKey = null;
                commit('setAccountsRequest', {loading: false, message: error});
            });
    },
    deleteOrderedProduct({dispatch, commit}, id) {
        commit('setOrderedProductsRequest', {loading: true});
        analyticsService.deleteOrderedProduct(id)
            .then((response) => {
                dispatch('getOrderedProductsHistory', {force: true});
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => commit('setOrderedProductsRequest', {loading: false, message: error}));
    },
    deleteOrderedProductWithAccount({dispatch, commit}, {accountId, productId}) {
        commit('setOrderedProductsRequest', {loading: true});
        analyticsService.deleteOrderedProduct(productId)
            .then((response) => {
                dispatch('getClientAccountSummary', accountId);
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => commit('setOrderedProductsRequest', {loading: false, message: error}));
    },
    updateOrderedProduct({dispatch, commit}, orderedProduct) {
        commit('setAccountSummaryRequest', {loading: true});
        analyticsService.updateOrderedProduct(orderedProduct)
            .then((response) => {
                dispatch('getClientAccountSummary', state.clientAccountSummaryData.clientAccount.clientAccountId);
                commit('setAccountSummaryRequest', {loading: false});
            })
            .catch((error) => commit('setAccountSummaryRequest', {loading: false, message: error}));
    },
    deleteAccount({dispatch, commit}, accountId){
        commit('setOrderedProductsRequest', {loading: true});
        analyticsService.deleteAccount(accountId)
            .then((response) => {
                dispatch('getClientAccountSummary', accountId);
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => commit('setOrderedProductsRequest', {loading: false, message: error}));
    },
    deleteAccountWithHistory({dispatch, commit}, accountId) {
        commit('setAccountsRequest', {loading: true});
        analyticsService.deleteAccount(accountId)
            .then((response) => {
                dispatch('getAccountsHistory', {force: true});
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => commit('setAccountsRequest', {loading: false, message: error}));
    },
    deleteAccountWithHistoryBatch({ dispatch, commit }, accountIds) {
        commit('setAccountsRequest', {loading: true});
        const ids = Array.isArray(accountIds)
            ? accountIds.map(p => (typeof p === 'object' ? p.clientAccountId : p))
            : [accountIds].filter(Boolean);
        analyticsService.deleteAccountBatch(ids)
            .then((response) => {
                dispatch('getAccountsHistory', {force: true});
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => commit('setAccountsRequest', {loading: false, message: error}));
    },
    deleteAccountBatch(context, accountIds) {
        const ids = Array.isArray(accountIds)
            ? accountIds.map(p => (typeof p === 'object' ? p.clientAccountId : p))
            : [accountIds].filter(Boolean);
        return analyticsService.deleteAccountBatch(ids)
            .then(() => {
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch(() => {
                toast.error(i18n.global.t("toast.deleteError"));
            });
    },
    getClientAccountSummary({commit, state}, id) {
        const requestId = ++accountSummaryRequestId;
        commit('setAccountSummaryRequest', {loading: true});
        if (String(state.clientAccountSummaryData?.clientAccount?.clientAccountId) !== String(id)) {
            commit('updateAccountSummaryData', null);
        }
        analyticsService.getClientAccountSummary(id)
            .then((response) => {
                if (requestId !== accountSummaryRequestId) return;
                commit('setAccountSummaryRequest', {loading: false});
                commit('updateAccountSummaryData', response.data);
            })
            .catch((error) => {
                if (requestId !== accountSummaryRequestId) return;
                commit('setAccountSummaryRequest', {loading: false, message: error});
            });
    },
    deleteTransaction({ commit, dispatch, state }, transaction) {
        commit('setAccountSummaryRequest', { loading: true });

        return analyticsService.deleteTransaction(transaction)
            .then(() => {
                toast.success(i18n.global.t("toast.deleteSuccess"));
                const accountId = state.clientAccountSummaryData?.clientAccount?.clientAccountId;
                if (accountId) {
                    return dispatch('getClientAccountSummary', accountId);
                }
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.deleteError"));

                commit('setAccountSummaryRequest', { loading: false, message: error });
            })
            .finally(() => {
                commit('setAccountSummaryRequest', { loading: false });
            });
    },
    updateTransaction({ commit, dispatch, state }, { transaction, newType }) {
        commit('setAccountSummaryRequest', { loading: true });

        return analyticsService.updateTransaction(transaction, newType)
            .then(() => {
                toast.success(i18n.global.t("toast.successEdit"));
                const accountId = state.clientAccountSummaryData?.clientAccount?.clientAccountId;
                if (accountId) {
                    return dispatch('getClientAccountSummary', accountId);
                }
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.errorEdit"));
                commit('setAccountSummaryRequest', { loading: false, message: error });
            })
            .finally(() => {
                commit('setAccountSummaryRequest', { loading: false });
            });
    }
}

const mutations = {
    setMessage(state, message) {
        state.message = message;
    },
    setPaymentsRequest(state, {loading, message}){
        state.paymentsRequest = {
            loading, message
        };
    },
    allowSkeletonOnce(state) {
        state.shouldWeLoadAgain = true;
        state.firstLoading = false;
    },
    updatePaymentsData(state, paymentsData){
        state.paymentsData = paymentsData;
    },
    setOrderedProductsRequest(state, {loading, message}){
        state.orderedProductsRequest = {
            loading, message
        };
    },
    setOrderedProductsDataKey(state, key) {
        state.orderedProductsDataKey = key;
    },
    setAccountsDataKey(state, key) {
        state.accountsDataKey = key;
    },
    updateOrderedProductsData(state, orderedProductsData){
        state.orderedProductsData = orderedProductsData;
    },
    setAccountsRequest(state, {loading, message}){
        state.accountsRequest = {
            loading, message
        };
    },
    resetDashboardData(state) {
        state.dashboardRequest = { loading: false, data: null };
        state.rawDashboardData = null;
        state.dashboardAccountsChartData = null;
        state.dashboardSalesChartData = null;
        state.dashboardDailySalesFormattedChartData = null;
        state.dashboardRushFormattedChartData = null;
        state.dashboardTopSalesFormattedChartData = null;
        state.dashboardTopSalesByDeviceFormattedChartData = null;
    },
    updateAccountsData(state, accountsData){
        state.accountsData = accountsData;
    },
    setQueryStartDateTime(state, startDate) {
        state.queryStartDateTime = startDate;
    },
    setQueryEndDateTime(state, endDate) {
        state.queryEndDateTime = endDate;
    },
    setQueryUserId(state, userId) {
        state.queryUserId = userId;
    },
    setQueryShiftId(state, shiftId) {
        state.queryShiftId = shiftId;
    },
    setQueryUserName(state, userName) {
        state.queryUserName = userName;
    },
    clearAccountsFilters(state) {
        state.queryUserId = null;
        state.queryShiftId = null;
        state.queryUserName = null;
    },
    updateAccountSummaryData(state, summary) {
        state.clientAccountSummaryData = summary;
    },
    setAccountSummaryRequest(state, {loading, message}){
        state.clientAccountSummaryRequest = {
            loading, message
        };
    },
    setDashboardRequest(state, data) {
        state.dashboardRequest = data;
    },
    setDashboardPartnerSourceRequest(state, data){
        state.dashboardPartnerSourceRequest = data
    },
    setFirstLoading(state, value){
        if (value === true){
            if (state.shouldWeLoadAgain){
                state.firstLoading = true
                state.shouldWeLoadAgain = false
            }
        }else{
            state.firstLoading = false
        }
    },
    setDashboardChartData(state, data) {
        state.rawDashboardData = data;
        state.dashboardAccountsChartData = computeAccountsChartData(data);
        state.dashboardSalesChartData = computeSalesChartData(data);
        state.dashboardDailySalesFormattedChartData = computeFormattedChartData(data.salesChartData);
        state.dashboardRushFormattedChartData = computeFormattedChartData(data.rushHourChartData);
        state.dashboardTopSalesFormattedChartData = computeFormattedChartData(data.topSellerChartData)
        state.dashboardTopSalesByDeviceFormattedChartData = computeFormattedChartData(data.salesByDeviceChartData)
    },
    setDashboardDataPartnerSource(state, data) {
        state.shiftOpen = data.shiftOpen;
        state.allOrgs = data.allOrgs;
        state.deviceModelChartData = data.deviceModelChartData;
    },
    setProductGroupsDashboardData(state, data) {
        state.productGroupsDashboardData = data;
    }
}

function computeAccountsChartData(data) {
    let accountsData = data.accountsData;
    let labels = ["Tables", "Takeaway", "Delivery"];
    let chartData = [accountsData.tableCount, accountsData.takeawayCount, accountsData.deliveryCount];
    let formattedChartData = new FormattedChartData(chartData, labels)
    let totalCount = accountsData.totalCount;
    return  {
        totalCount:  totalCount,
        chartData:  formattedChartData
    }
}

function computeSalesChartData(data) {
    let salesData = data.salesData;
    let labels = ["Cash", "Card", "Transfer"];
    let chartData = [salesData.totalCash, salesData.totalCard, salesData.totalBankTransfer];
    let formattedChartData = new FormattedChartData(chartData, labels)
    let totalCount = salesData.total.toFixed(2);
    return {
        totalCount:  totalCount,
        chartData:  formattedChartData
    }
}

function computeFormattedChartData(data) {
    let labels = data.map(item => item.name);
    let count = data.map(item => item.count);
    let formattedChartData = new FormattedChartData(count, labels);
    return formattedChartData;
}

export const analytics = {
    namespaced: true,
    state,
    actions,
    mutations,
}